import React, { useEffect, useState } from "react";
import globalAxios from "axios";
import { ChangeDiffViewer } from "../diff/ChangeDiffViewer";
import { DeploymentVisualPair, useDeploymentRenderPair } from "./DeploymentVisualPair";

/**
 * Socle partagé de la comparaison brouillon/déployé : le hook interroge
 * GET /api/deployment/diff (hash d'abord, payloads seulement quand ils divergent) et
 * DeploymentDiffBody rend l'écart via le ChangeDiffViewer commun avec /audit. Consommé par la
 * modale (outillage) et par le panneau de droite de la page Publication (vue maître-détail).
 */

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

export interface DeploymentDiffResponse {
	supported: boolean;
	reason?: string | null;
	path?: string | null;
	identical: boolean;
	expectedHash?: string | null;
	deployedHash?: string | null;
	deployedAt?: string | null;
	expected?: unknown;
	deployed?: unknown;
	deployedMissing: boolean;
	expectedAbsent: boolean;
}

/* File d'attente : un groupe sélectionné compare jusqu'à 25 contenus, on borne à 3 requêtes
   simultanées pour ne pas assommer l'API (chaque diff rejoue le pipeline de déploiement). */
const MAX_CONCURRENT = 3;
let active = 0;
const waiters: Array<() => void> = [];
const acquire = () =>
	new Promise<void>((resolve) => {
		if (active < MAX_CONCURRENT) {
			active++;
			resolve();
		} else {
			waiters.push(() => {
				active++;
				resolve();
			});
		}
	});
const release = () => {
	active = Math.max(0, active - 1);
	const next = waiters.shift();
	if (next) next();
};

export interface DeploymentDiffState {
	loading: boolean;
	error: string | null;
	diff: DeploymentDiffResponse | null;
}

/** Charge la comparaison d'un contenu contre une cible ; enabled=false = ne rien faire. */
export const useDeploymentDiff = (
	contentId: string | null | undefined,
	target: "preview" | "production",
	enabled = true
): DeploymentDiffState => {
	const [state, setState] = useState<DeploymentDiffState>({ loading: false, error: null, diff: null });

	useEffect(() => {
		if (!enabled || !contentId) {
			setState({ loading: false, error: null, diff: null });
			return;
		}
		let cancelled = false;
		setState({ loading: true, error: null, diff: null });
		(async () => {
			await acquire();
			try {
				if (cancelled) return;
				const res = await globalAxios.get<DeploymentDiffResponse>(`${apiBase}/api/deployment/diff`, {
					params: { contentId, target },
					withCredentials: true
				});
				if (!cancelled) setState({ loading: false, error: null, diff: res.data });
			} catch (err: any) {
				if (!cancelled)
					setState({
						loading: false,
						error:
							err?.response?.data?.message ??
							"La comparaison n'a pas pu être calculée. Réessayez ou consultez les journaux.",
						diff: null
					});
			} finally {
				release();
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [contentId, target, enabled]);

	return state;
};

/** Verdict compact d'une comparaison, pour les pastilles d'état des listes. */
/** True when the diff of a « {path} » template ships a rows summary instead of a page payload. */
export const isTemplateSummary = (diff: DeploymentDiffResponse | null | undefined): boolean =>
	Boolean(diff && ((diff.expected as Record<string, unknown> | null)?.kind === "dynamic-template" || (diff.deployed as Record<string, unknown> | null)?.kind === "dynamic-template"));

export type DiffVerdict = "loading" | "error" | "unsupported" | "removal" | "new" | "identical" | "divergent";

export const diffVerdict = (state: DeploymentDiffState): DiffVerdict => {
	if (state.loading) return "loading";
	if (state.error) return "error";
	const d = state.diff;
	if (!d || !d.supported) return "unsupported";
	if (d.expectedAbsent) return "removal";
	if (d.identical) return "identical";
	if (d.deployedMissing) return "new";
	return "divergent";
};

const VERDICT_META: Record<DiffVerdict, { label: string; className: string }> = {
	loading: { label: "comparaison…", className: "border-slate-200 bg-slate-50 text-slate-400" },
	error: { label: "erreur", className: "border-rose-200 bg-rose-50 text-rose-700" },
	unsupported: { label: "non comparable", className: "border-slate-200 bg-slate-50 text-slate-500" },
	removal: { label: "retrait", className: "border-rose-200 bg-rose-50 text-rose-700" },
	new: { label: "nouveau", className: "border-sky-200 bg-sky-50 text-sky-700" },
	identical: { label: "identique", className: "border-emerald-200 bg-emerald-50 text-emerald-700" },
	divergent: { label: "diffère", className: "border-amber-200 bg-amber-50 text-amber-700" }
};

export const DiffVerdictChip: React.FC<{ verdict: DiffVerdict }> = ({ verdict }) => {
	const meta = VERDICT_META[verdict];
	return (
		<span className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${meta.className}`}>
			{meta.label}
		</span>
	);
};

/** Le mode « Rendu côte à côte » : la paire n'est chargée qu'au premier affichage du mode. */
const VisualPairMode: React.FC<{ contentId: string; target: "preview" | "production" }> = ({ contentId, target }) => {
	const state = useDeploymentRenderPair(contentId, target, true);
	return <DeploymentVisualPair state={state} target={target} />;
};

/** Corps de la comparaison : tous les états (chargement, erreur, identique, retrait…) + le diff. */
export const DeploymentDiffBody: React.FC<{
	state: DeploymentDiffState;
	target: "preview" | "production";
	/** compact : padding réduit pour les lignes de groupe. */
	compact?: boolean;
	/** Id du contenu : active le troisième mode « Rendu côte à côte » (deux pages rendues). */
	contentId?: string | null;
}> = ({ state, target, compact = false, contentId = null }) => {
	const { loading, error, diff } = state;
	const pad = compact ? "px-4 py-6" : "px-5 py-16";
	const targetLabel = target === "production" ? "production" : "preview";

	if (loading) return <div className={`${pad} text-center text-sm text-slate-500`}>Comparaison en cours…</div>;
	if (error) return <div className={`${pad} text-center text-sm text-rose-600`}>{error}</div>;
	if (!diff) return null;
	if (!diff.supported)
		return <div className={`${pad} text-center text-sm text-slate-500`}>{diff.reason ?? "Ce contenu n'a pas d'artefact comparable."}</div>;
	if (diff.identical)
		return (
			<div className={`${pad} text-center text-sm text-emerald-700`}>
				Ce contenu est déjà à jour en {targetLabel} : le brouillon et l'artefact déployé sont identiques.
			</div>
		);
	if (diff.expectedAbsent)
		return (
			<div className={`${pad} text-center text-sm text-slate-600`}>
				Ce contenu est supprimé côté admin : la publication RETIRERA l'artefact encore présent en {targetLabel}.
			</div>
		);
	return (
		<div className="space-y-3">
			{isTemplateSummary(diff) ? (
				<p className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-2 text-xs text-sky-800" data-testid="diff-template-summary">
					Gabarit {"{path}"} : ses fiches sont des lignes de la base de lecture. Le résumé ci-dessous liste les comptes et les urls en écart
					(manquantes, différentes, en trop) ; le rendu côte à côte d'une fiche montre le détail.
				</p>
			) : diff.deployedMissing && (
				<p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
					Rien n'est encore déployé en {targetLabel} pour ce contenu : tout apparaît comme ajouté.
				</p>
			)}
			<ChangeDiffViewer
				before={diff.deployed ?? {}}
				after={diff.expected ?? {}}
				beforeLabel={diff.deployedMissing ? "Rien de déployé" : `Déployé (${targetLabel})`}
				afterLabel="Brouillon admin"
				visual={contentId ? <VisualPairMode contentId={contentId} target={target} /> : undefined}
			/>
		</div>
	);
};
